import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import TaskUtils from 'services/TaskUtils';
import { Shift } from 'generated/api';

export interface AwayInfo {
  away: boolean;
  reason?: string;
}

const NOT_AWAY: AwayInfo = { away: false };

/**
 * Advisory OOO check for a task assignee. Fetches the assignee's non-working
 * shifts via getUserNonWorkingShifts — which is keyed on user_id (so it is
 * correct across clinics, unlike the clinic-scoped getDayShifts) and already
 * server-filtered to approved non-working — and reports whether any of them
 * cover today. Read-only: a miss simply shows no warning, and errors resolve to
 * "not away" with no toast (the thunk is dispatched without error options).
 *
 * Results are cached per user_id for the life of the hook so toggling the
 * assignee back and forth does not refetch, and in-flight responses are guarded
 * against a fast assignee change so a stale reply can't paint the wrong warning.
 */
export default function useAssigneeAway(userId?: string): AwayInfo {
  const dispatch = useAppDispatch();
  const [info, setInfo] = useState<AwayInfo>(NOT_AWAY);
  const cache = useRef<Record<string, AwayInfo>>({});

  useEffect(() => {
    if (!userId || userId === 'none') {
      setInfo(NOT_AWAY);
      return;
    }

    const cached = cache.current[userId];
    if (cached) {
      setInfo(cached);
      return;
    }

    let cancelled = false;
    setInfo(NOT_AWAY);

    dispatch(api.Shifts.getUserNonWorkingShifts({ userId })).then((shifts?: Shift[]) => {
      if (cancelled) return;
      const awayShift = TaskUtils.getAwayShiftOn(shifts || [], moment().unix());
      const result: AwayInfo = awayShift
        ? { away: true, reason: awayShift.non_working_reason }
        : NOT_AWAY;
      cache.current[userId] = result;
      setInfo(result);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, dispatch]);

  return info;
}
